import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import { addFaqSchema } from "components/Common/validation"
import { addFaq, getFaqDetails, updateFaq } from "store/actions/faqAction"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import usePageContentAppType from "hooks/usePageContentAppType"
import { withAppTypeQuery } from "utils/pageContentPermissions"

const initialValues = {
  title: "",
  titleAr: "",
  description: "",
  descriptionAr: "",
}

const AddFaq = ({ mode }) => {
  const {
    t,
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${t("FAQ")}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const {
    activeAppType,
    usersPermissions,
    hasAnyAccess,
  } = usePageContentAppType("faq")

  useEffect(() => {
    if (!hasAnyAccess) {
      navigate("/dashboard")
      return
    }
    if (mode === "add" && !usersPermissions.create) {
      navigate("/dashboard")
    } else if (mode === "edit" && !usersPermissions.edit) {
      navigate("/dashboard")
    }
  }, [hasAnyAccess, mode, usersPermissions, navigate])

  const { loading, isLoading, faqDetails } = useSelector(state => state.Faqs)
  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    if (mode === "edit" && id) {
      fetchFaqDetails()
    }
  }, [mode, id, activeAppType])

  const fetchFaqDetails = () => {
    const payload = { id, appType: activeAppType }
    dispatch(getFaqDetails(payload))
  }

  useEffect(() => {
    if (faqDetails && !loading && mode === "edit") {
      const { title, titleAr, description, descriptionAr } = faqDetails
      setFormValues({
        title,
        titleAr,
        description,
        descriptionAr,
      })
    }
  }, [faqDetails, loading, mode])

  const handleOnSubmit = values => {
    if (mode === "add") {
      dispatch(addFaq({ ...values, appType: activeAppType }))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(withAppTypeQuery("/faq", activeAppType))
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...values,
        id,
        appType: activeAppType,
      }
      dispatch(updateFaq(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("FAQ")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addFaqSchema()}
                  validateOnBlur
                  validateOnChange={false}
                  onSubmit={handleOnSubmit}
                >
                  {({ touched, errors, submitCount }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="title">
                              <span className="text-danger">*</span>{" "}
                              {t("Title(English)")}
                            </label>
                            <Field
                              type="text"
                              id="title"
                              name="title"
                              placeholder={t("Title(English)")}
                              className={`form-control ${
                                (touched.title || submitCount > 0) &&
                                errors.title
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="title" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="titleAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Title(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="titleAr"
                              name="titleAr"
                              placeholder={t("Title(عربي)")}
                              className={`form-control ${
                                (touched.titleAr || submitCount > 0) &&
                                errors.titleAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="titleAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="description">
                              {t("Description(English)")}
                            </label>
                            <Field
                              as="textarea"
                              id="description"
                              name="description"
                              placeholder={t("Description(English)")}
                              className={`form-control ${
                                (touched.description || submitCount > 0) &&
                                errors.description
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="description" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="descriptionAr">
                              {t("Description(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              id="descriptionAr"
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              style={{ direction: "rtl" }}
                              className={`form-control ${
                                (touched.descriptionAr || submitCount > 0) &&
                                errors.descriptionAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="descriptionAr" />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end mt-3 mb-1">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddFaq
